import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { submitPrivateInquiry } from "@/lib/api";

const INQUIRY_TYPES = [
  "Private Jewelry Viewing",
  "Bespoke Design Consultation",
  "Product Information",
  "Availability & Pricing",
];

const CONTACT_METHODS = [
  "Email",
  "Phone",
  "WhatsApp",
  "Via Introducing Concierge",
];

const INITIAL = {
  full_name: "",
  email: "",
  preferred_contact: "Email",
  piece_of_interest: "",
  inquiry_type: "Private Jewelry Viewing",
  message: "",
};

/**
 * Full-page section + dialog for Ultra Fine Jewelry private inquiries.
 * Can be used in two modes:
 *   • As an inline section (open=null, render form inline)
 *   • As an overlay dialog (open=true, piece prop prefills piece_of_interest)
 *
 * Submits to the existing POST /api/private-inquiries endpoint, mapping extended
 * fields into the notes blob so no destructive backend changes are required.
 */
export default function UFJPrivateClientDialog({ open, piece, mode = "inquire", onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        ...INITIAL,
        piece_of_interest: piece?.name || "",
        inquiry_type:
          mode === "commission"
            ? "Bespoke Design Consultation"
            : mode === "pricing"
            ? "Availability & Pricing"
            : INITIAL.inquiry_type,
      });
    }
  }, [open, piece, mode]);

  if (!open) return null;

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      // Compose a structured notes blob including the extended fields so no
      // backend schema change is required for the first launch.
      const parts = [
        `Inquiry Type: ${form.inquiry_type}`,
        `Preferred Contact: ${form.preferred_contact}`,
        form.piece_of_interest ? `Piece of Interest: ${form.piece_of_interest}` : null,
        form.message ? `\nMessage:\n${form.message}` : null,
      ].filter(Boolean);

      await submitPrivateInquiry({
        inquiry_type:
          form.inquiry_type.toLowerCase().includes("viewing") ? "viewing"
          : form.inquiry_type.toLowerCase().includes("pric") ? "pricing"
          : "appointment",
        full_name: form.full_name,
        email: form.email,
        country: null,
        piece: form.piece_of_interest || piece?.name || "Ultra Fine Jewelry",
        notes: parts.join("\n"),
      });
      toast.success(
        "Thank you for your inquiry. A PiérrecDavís concierge will respond personally within 72 hours."
      );
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    mode === "commission"
      ? "Begin Your Private Commission"
      : piece?.name
      ? `Inquire — ${piece.name}`
      : "The Private Client Experience";

  const subtitle =
    mode === "commission"
      ? "A commission begins with a conversation. Share a few details and a maison concierge will arrange a private audience."
      : "A maison concierge will reply personally, in confidence.";

  return (
    <div
      data-testid="ufj-private-client-dialog"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-8"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
        data-testid="ufj-dialog-backdrop"
      />
      <div className="relative w-full max-w-2xl bg-black border border-[var(--pd-border)] p-8 md:p-12 max-h-[92vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-white/60 hover:text-[var(--pd-gold)] transition-colors"
          data-testid="ufj-dialog-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <p className="eyebrow" data-testid="ufj-dialog-eyebrow">
          PiérrecDavís · Private Client Experience
        </p>
        <h3
          className="mt-4 font-serif-display text-3xl md:text-4xl text-white tracking-[0.06em]"
          data-testid="ufj-dialog-title"
        >
          {title}
        </h3>
        <p className="mt-2 text-white/60 text-sm">{subtitle}</p>

        <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          <label className="block">
            <span className="block eyebrow mb-2">Full Name</span>
            <input
              className="pd-input"
              value={form.full_name}
              onChange={update("full_name")}
              required
              data-testid="ufj-input-name"
            />
          </label>
          <label className="block">
            <span className="block eyebrow mb-2">Email</span>
            <input
              type="email"
              className="pd-input"
              value={form.email}
              onChange={update("email")}
              required
              data-testid="ufj-input-email"
            />
          </label>

          <label className="block">
            <span className="block eyebrow mb-2">Preferred Contact</span>
            <select
              className="pd-input bg-black"
              value={form.preferred_contact}
              onChange={update("preferred_contact")}
              data-testid="ufj-input-contact"
            >
              {CONTACT_METHODS.map((m) => (
                <option key={m} value={m} className="bg-black text-white">
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block eyebrow mb-2">Type of Inquiry</span>
            <select
              className="pd-input bg-black"
              value={form.inquiry_type}
              onChange={update("inquiry_type")}
              data-testid="ufj-input-inquiry-type"
            >
              {INQUIRY_TYPES.map((t) => (
                <option key={t} value={t} className="bg-black text-white">
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className="block md:col-span-2">
            <span className="block eyebrow mb-2">Jewelry Piece of Interest (optional)</span>
            <input
              className="pd-input"
              value={form.piece_of_interest}
              onChange={update("piece_of_interest")}
              placeholder={piece?.name || "e.g. Statement Necklace"}
              data-testid="ufj-input-piece"
            />
          </label>

          <label className="block md:col-span-2">
            <span className="block eyebrow mb-2">Message (optional)</span>
            <textarea
              rows={3}
              className="pd-input resize-none"
              value={form.message}
              onChange={update("message")}
              data-testid="ufj-input-message"
            />
          </label>

          <div className="md:col-span-2 flex items-center justify-between gap-4 pt-3">
            <p className="text-white/40 text-[0.6rem] uppercase tracking-[0.32em] max-w-sm">
              All submissions are held in strict confidence
            </p>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost"
                data-testid="ufj-dialog-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold disabled:opacity-60"
                data-testid="ufj-dialog-submit"
              >
                <span>{submitting ? "Sending…" : "Send Inquiry"}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
